/**
 * Ce que les calques montés cachent du canvas, et ce que le studio peut donc
 * cesser de dessiner.
 *
 * Pur : ni React, ni DOM, ni Pixi. Ce module ne fait que mettre en commun des
 * déclarations et en tirer une union ; qui déclare quoi est écrit là où la
 * géométrie l'est — `StudioFrame` pour ses deux slots opaques, `studio-page`
 * pour les écrans de parcours qui se posent eux-mêmes.
 *
 * ## Pourquoi on déclare au lieu de mesurer
 *
 * Savoir si un canvas est recouvert n'est pas une question qu'on peut poser au
 * navigateur : `IntersectionObserver` ne voit que le défilement, sa version 2
 * n'existe que sur Chromium — or le produit tourne aussi dans une WebView
 * WKWebKit — et `elementFromPoint` traverse les calques en
 * `pointer-events: none`, dont sont faits précisément les écrans de parcours.
 *
 * Une déclaration, en revanche, vit à côté de ce qui la rend vraie : le slot
 * qui pose `bg-overlay-deep` et le `bottom` qui le clampe sont écrits dans la
 * même expression. Un calque qui deviendrait translucide changerait les deux
 * d'un même geste.
 *
 * ## Trois surfaces, et non un booléen
 *
 * Le canvas n'est pas d'un seul tenant : il porte le rouleau, le clavier et le
 * rail de minimap, et presque aucun calque ne les couvre tous les trois. Le
 * slot standard s'arrête au-dessus des touches et à gauche de la minimap ; le
 * parcours harmonique prend le clavier mais laisse la minimap ; seul l'écran
 * de présentation d'un morceau prend tout.
 *
 * Un booléen aurait donc été faux presque partout : soit on ne coupait rien là
 * où il y avait presque tout à couper, soit on figeait une minimap qu'on voit
 * bouger.
 */

/** Les trois surfaces que le canvas du studio dessine. */
export type StudioSurface = "roll" | "piano" | "minimap";

/** Toutes, pour les calques qui prennent le canvas entier. */
export const ALL_SURFACES: readonly StudioSurface[] = ["roll", "piano", "minimap"];

/**
 * Les déclarations en cours, et ce qu'elles cachent ensemble.
 *
 * Un registre et non une valeur : plusieurs calques peuvent être montés en même
 * temps — les réglages ouverts depuis un parcours, par exemple — et chacun doit
 * pouvoir se retirer sans effacer ce que l'autre affirme. C'est la même raison
 * qui fait qu'on compte les verrous plutôt que de tenir un drapeau.
 */
export class CanvasCovers {
  private readonly held = new Map<object, readonly StudioSurface[]>();

  /**
   * Déclarer ce qu'un calque cache. Rend `true` si l'union en a changé.
   *
   * Le jeton est l'identité du déclarant, et non ce qu'il déclare : c'est ce
   * qui rend le retrait exact quand deux calques cachent la même surface.
   */
  hold(token: object, surfaces: readonly StudioSurface[]): boolean {
    const before = this.hidden;

    this.held.set(token, surfaces);

    return !same(before, this.hidden);
  }

  /** Retirer une déclaration. Rend `true` si l'union en a changé. */
  release(token: object): boolean {
    if (!this.held.has(token)) {
      return false;
    }

    const before = this.hidden;

    this.held.delete(token);

    return !same(before, this.hidden);
  }

  /** Ce qui est caché par au moins un calque. */
  get hidden(): ReadonlySet<StudioSurface> {
    const union = new Set<StudioSurface>();

    for (const surfaces of this.held.values()) {
      for (const surface of surfaces) {
        union.add(surface);
      }
    }

    return union;
  }

  /**
   * Le canvas est-il entièrement caché ?
   *
   * C'est le seul cas où l'on peut cesser de **présenter** : tant qu'une
   * surface se voit, une image doit être produite, même si tout le reste de la
   * scène est éteint.
   */
  get blind(): boolean {
    return ALL_SURFACES.every((surface) => this.hidden.has(surface));
  }
}

/** Deux unions portent-elles les mêmes surfaces ? */
export function same(one: ReadonlySet<StudioSurface>, other: ReadonlySet<StudioSurface>): boolean {
  if (one.size !== other.size) {
    return false;
  }

  for (const surface of one) {
    if (!other.has(surface)) {
      return false;
    }
  }

  return true;
}
